import { CameraStatusEnum } from '@api/enum';
import { PermissionEnum } from '@api/enum';
import { ICameraCCTV } from '@api/models/camera-cctv.model';
import { Button, Card, LinearProgress } from '@hyperflake/react-ui-library';
import { FormHelperText, Label } from '@library/forms';
import { CircularProgress } from '@library/loaders/components';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import http from 'httpConfig/http';
import upperCase from 'lodash.uppercase';
import useCctvCameraUpdateForm from 'modules/cctv-cameras/hooks/useCctcCameraUpdateForm';
import useCctvCameraData from 'modules/cctv-cameras/hooks/useCctvCameraData';
import FormikDatePicker from 'modules/shared/components/FormFields/FormikDatePicker';
import RequiredMark from 'modules/shared/components/FormFields/RequiredMark';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { FormikInput, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import { getResellerIdFromRedux } from 'modules/shared/helpers/shared.utils';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ChangeProjectForm from '../ChangeProjectForm/ChangeProjectForm';
import CctvChannelUpdateForm from '../CctvChannelUpdateForm/CctvChannelUpdateForm';
import MapCameraControlsPanel from 'modules/shared/features/camera-map/components/MapCameraControlsPanel/MapCameraControlsPanel';
import { GoogleMap, useGoogleMapScriptLoader } from 'modules/shared/features/google-map';
import MapCameraOverlay from 'modules/shared/features/camera-map/components/MapCameraOverlay/MapCameraOverlay';
import { toMapCameraFromCctv } from 'modules/shared/features/camera-map/helpers/mapCamera.adapters';
import { timezoneList } from 'modules/shared/helpers/shared.constants';

const CctvCameraEditForm = () => {
    const { clientId, projectId, cctvCameraId } = useParams();
    const { cctvCamera, isLoading, setData } = useCctvCameraData(clientId, projectId, cctvCameraId);

    const CctvCameraUpdateForm = useCctvCameraUpdateForm({ clientId, projectId, cctvCamera });

    const [cameraAngle, setCameraAngle] = useState(0);
    const [cameraRange, setCameraRange] = useState(0);
    const [cameraFOV, setCameraFOV] = useState(0);

    // Location editing
    const [isEditingLocation, setIsEditingLocation] = useState(false);
    const [tempLatitude, setTempLatitude] = useState('');
    const [tempLongitude, setTempLongitude] = useState('');

    const { isLoaded } = useGoogleMapScriptLoader();
    const location = useLocation();

    // Initialize pending map values when cctvCamera loads/changes
    useEffect(() => {
        if (!cctvCamera) return;

        setCameraAngle(Number(cctvCamera.angle ?? 0));
        setCameraRange(Number(cctvCamera.distance ?? 0));
        setCameraFOV(Number(cctvCamera.fieldOfView ?? 0));

        setTempLatitude(cctvCamera.location?.latitude?.toString?.() ?? '');
        setTempLongitude(cctvCamera.location?.longitude?.toString?.() ?? '');
    }, [cctvCamera]);

    const cameraPosition = useMemo(() => {
        const lat = Number(cctvCamera?.location?.latitude);
        const lng = Number(cctvCamera?.location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
    }, [cctvCamera?.location?.latitude, cctvCamera?.location?.longitude]);

    const tempMarkerPosition = useMemo(() => {
        if (!isEditingLocation) return null;
        const lat = Number(tempLatitude);
        const lng = Number(tempLongitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
    }, [isEditingLocation, tempLatitude, tempLongitude]);

    const hasUnsavedChanges = useMemo(() => {
        if (!cctvCamera) return false;

        const baseAngle = Number(cctvCamera.angle ?? 0);
        const baseRange = Number(cctvCamera.distance ?? 0);
        const baseFov = Number(cctvCamera.fieldOfView ?? 0);

        const baseLat = cctvCamera.location?.latitude?.toString?.() ?? '';
        const baseLng = cctvCamera.location?.longitude?.toString?.() ?? '';

        return (
            cameraAngle !== baseAngle ||
            cameraRange !== baseRange ||
            cameraFOV !== baseFov ||
            tempLatitude !== baseLat ||
            tempLongitude !== baseLng ||
            isEditingLocation
        );
    }, [cctvCamera, cameraAngle, cameraRange, cameraFOV, tempLatitude, tempLongitude, isEditingLocation]);

    const cameraStatusOptions = Object.values(CameraStatusEnum).map((status) => ({
        label: upperCase(status),
        value: status,
    }));

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(
        ['clients', clientId, 'projects', projectId, 'cc-cameras', 'storage_names'],
        async () => {
            const { data } = await http.get(
                `${
                    import.meta.env.VITE_APP_API_URL
                }/${getResellerIdFromRedux()}/clients/${clientId}/projects/${projectId}/cameras/storage-names`
            );
            return data;
        }
    );

    // const transformData = (values: any) => ({
    //     ...values,
    //     installationDate: moment(values.installationDate).format('YYYYMMDD'),
    // });

    const handleSubmit = async (values: any) => {
        try {
            // const tranformedData = transformData(values);
            await CctvCameraUpdateForm.submit(values);
            toast.success('Live Camera Updated Successfully');
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Oops something went wrong.';
            toast.error(msg);
        }
    };

    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [{ label: 'Select Storage', value: '' }, ...storages.map((val: string) => ({ label: val, value: val }))];
    }, [storages]);

    const { hasPermission } = useAuthPermission();

    const defaultChannelOptions = useMemo(
        () =>
            (cctvCamera?.channels || []).map((ch) => ({
                label: `${ch.number}`,
                value: ch.number,
            })),
        [cctvCamera]
    );

    const handleLocationChange = useCallback(() => {
        // Apply / toggle edit mode
        if (isEditingLocation) {
            const lat = Number(tempLatitude);
            const lng = Number(tempLongitude);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                toast.error('Please enter valid coordinates');
                return;
            }

            // ✅ Update local data immediately for preview (pending)
            if (cctvCamera) {
                setData({
                    ...cctvCamera,
                    location: {
                        ...(cctvCamera.location ?? {}),
                        latitude: tempLatitude,
                        longitude: tempLongitude,
                    },
                });
            }
        }

        setIsEditingLocation((v) => !v);
    }, [isEditingLocation, tempLatitude, tempLongitude, cctvCamera, setData]);

    const handleMapClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!isEditingLocation || !e.latLng) return;

            setTempLatitude(e.latLng.lat().toFixed(6));
            setTempLongitude(e.latLng.lng().toFixed(6));
            toast.info('Location selected on map');
        },
        [isEditingLocation]
    );

    const handleMarkerDragEnd = useCallback((pos: google.maps.LatLngLiteral) => {
        setTempLatitude(pos.lat.toFixed(6));
        setTempLongitude(pos.lng.toFixed(6));
        toast.info('Marker position updated');
    }, []);

    const handleLoad = useCallback(
        (map: google.maps.Map) => {
            map.addListener('click', handleMapClick);

            // optional: stabilize zoom after load
            setTimeout(() => {
                map.setZoom(15);
            }, 600);
        },
        [handleMapClick]
    );

    const timezoneOption = useMemo(() => {
        return [
            {
                label: 'Select Timezone',
                value: '',
            },
            ...timezoneList.map((tz) => ({
                label: tz.label,
                value: tz.value,
            })),
        ];
    }, []);

    // Save “pending” map values into backend
    const handleSaveCameraView = useCallback(async () => {
        try {
            if (!cctvCamera) return;

            const lat = Number(tempLatitude);
            const lng = Number(tempLongitude);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                toast.error('Please enter valid coordinates');
                return;
            }

            const updatedCctcCamera = await CctvCameraUpdateForm.submit({
                ...CctvCameraUpdateForm.initialValues,
                location: { latitude: tempLatitude, longitude: tempLongitude },
                angle: cameraAngle,
                distance: cameraRange,
                fieldOfView: cameraFOV,
            });
            setData(updatedCctcCamera);

            toast.success('Camera view settings updated successfully');
            setIsEditingLocation(false);
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Failed to update camera view settings';
            toast.error(msg);
        }
    }, [cctvCamera, tempLatitude, tempLongitude, cameraAngle, cameraRange, cameraFOV, CctvCameraUpdateForm]);

    useEffect(() => {
        const shouldScroll = location.hash === '#map-preview' || (location.state as any)?.focus === 'map-preview';
        if (!shouldScroll) return;

        let tries = 0;
        const tick = () => {
            const el = document.getElementById('map-preview');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
            tries += 1;
            if (tries < 20) setTimeout(tick, 100); // retry up to ~2s
        };

        tick();
    }, [location.hash, location.state]);

    return (
        <PageTemplate>
            <PageHeading>Edit Live Camera</PageHeading>
            {isLoading ? (
                <LinearProgress />
            ) : (
                <div>
                    <Formik
                        initialValues={CctvCameraUpdateForm.initialValues}
                        enableReinitialize
                        validationSchema={CctvCameraUpdateForm.schema}
                        onSubmit={handleSubmit}
                    >
                        {({ errors }) => {
                            console.log('validation error', errors);
                            return (
                                <Form>
                                    {/* BASIC DETAILS inside Formik */}
                                    <fieldset disabled={!hasPermission(PermissionEnum.LIVE_CAMERA_UPDATE)}>
                                        <Card className="mt-4">
                                            <Card.Header>Basic Details</Card.Header>
                                            <Card.Body>
                                                <div>
                                                    <div>
                                                        <Label>
                                                            Name <RequiredMark />
                                                        </Label>
                                                        <FormikInput type="text" name="name" />
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="name" />
                                                        </FormHelperText>
                                                    </div>

                                                    <div>
                                                        <Label>
                                                            Installation Date <RequiredMark />
                                                        </Label>
                                                        <FormikDatePicker
                                                            name="installationDate"
                                                            maxDate={new Date()}
                                                        />
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="installationDate" />
                                                        </FormHelperText>
                                                    </div>

                                                    <div>
                                                        <Label>
                                                            QLive Unit <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikInput name="qLiveUnit" />
                                                        </div>
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="qLiveUnit" />
                                                        </FormHelperText>
                                                    </div>

                                                    <div>
                                                        <Label>
                                                            Storage <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect name="storage" options={storageOption} />
                                                            {storageIsLoading && <CircularProgress size={14} />}
                                                        </div>
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="storage" />
                                                        </FormHelperText>
                                                    </div>
                                                    <div>
                                                        <Label>Timezone</Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect name="timezone" options={timezoneOption} />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="timezone" />
                                                        </FormHelperText>
                                                    </div>

                                                    <div>
                                                        <Label>Status</Label>
                                                        <FormikSelect name="status" options={cameraStatusOptions} />
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="status" />
                                                        </FormHelperText>
                                                    </div>
                                                    <div>
                                                        <Label>Default Channel</Label>
                                                        <FormikSelect
                                                            name="defaultChannelNumber"
                                                            options={[
                                                                { label: 'Select default channel', value: '' },
                                                                ...defaultChannelOptions,
                                                            ]}
                                                            // placeholder="Select default channel"
                                                            disabled={!defaultChannelOptions.length}
                                                        />
                                                    </div>

                                                    <div className="flex items-center gap-x-4 col-span-2 mt-4 md:mt-6">
                                                        <Label
                                                            htmlFor="isPrimary"
                                                            className="font-medium text-grayscale-900 mb-0"
                                                        >
                                                            Is Primary
                                                        </Label>
                                                        <FormikSwitch color="success" name="isPrimary" />
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        {/* LOCATION MOVED INSIDE FORMIK */}
                                        <Card className="mt-4">
                                            <Card.Header>Location</Card.Header>
                                            <Card.Body>
                                                <div className="grid grid-cols-2 gap-4 mt-4">
                                                    <div>
                                                        <Label>Latitude</Label>
                                                        <FormikInput type="text" name="location.latitude" />
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="location.latitude" />
                                                        </FormHelperText>
                                                    </div>
                                                    <div>
                                                        <Label>Longitude</Label>
                                                        <FormikInput type="text" name="location.longitude" />
                                                        <FormHelperText variant="error">
                                                            <ErrorMessage name="location.longitude" />
                                                        </FormHelperText>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="submit"
                                                    size="lg"
                                                    className="mt-4 w-28"
                                                    disabled={CctvCameraUpdateForm.isSubmitting}
                                                >
                                                    {CctvCameraUpdateForm.isSubmitting ? (
                                                        <CircularProgress size={20} color="inherit" />
                                                    ) : (
                                                        'Submit'
                                                    )}
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </fieldset>
                                </Form>
                            );
                        }}
                    </Formik>

                    {/* SEPARATE CHANNEL FORM (its own Formik + submit) */}
                    {cctvCamera && clientId && projectId && (
                        <CctvChannelUpdateForm
                            clientId={clientId}
                            projectId={projectId}
                            cctvCamera={cctvCamera as ICameraCCTV}
                        />
                    )}

                    <Card className="mt-4">
                        <Card.Header>Change Project</Card.Header>
                        <Card.Body>
                            <ChangeProjectForm cctvCamera={cctvCamera as ICameraCCTV} />
                        </Card.Body>
                    </Card>

                    <Card>
                        <section id="map-preview" className="scroll-mt-24">
                            <div className="mt-4">
                                <Card.Header>
                                    <h6>Map Preview</h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className=" h-[500px] w-full overflow-hidden rounded-xl border">
                                        <div className="h-full w-full relative">
                                            <MapCameraControlsPanel
                                                title="Camera View"
                                                isEditingLocation={isEditingLocation}
                                                tempLatitude={tempLatitude}
                                                tempLongitude={tempLongitude}
                                                onTempLatitudeChange={setTempLatitude}
                                                onTempLongitudeChange={setTempLongitude}
                                                onToggleEditOrApplyLocation={handleLocationChange}
                                                onCancelLocationEdit={() => {
                                                    setIsEditingLocation(false);
                                                    if (cctvCamera?.location) {
                                                        setTempLatitude(
                                                            cctvCamera.location.latitude?.toString?.() ?? ''
                                                        );
                                                        setTempLongitude(
                                                            cctvCamera.location.longitude?.toString?.() ?? ''
                                                        );
                                                    }
                                                }}
                                                hasUnsavedChanges={hasUnsavedChanges}
                                                cameraAngle={cameraAngle}
                                                cameraRange={cameraRange}
                                                cameraFOV={cameraFOV}
                                                onCameraAngleChange={setCameraAngle}
                                                onCameraRangeChange={setCameraRange}
                                                onCameraFOVChange={setCameraFOV}
                                                onSave={handleSaveCameraView}
                                                isSubmitting={CctvCameraUpdateForm.isSubmitting}
                                            />

                                            {/* Map with cursor change when editing */}
                                            <div
                                                className="h-full w-full"
                                                style={{ cursor: isEditingLocation ? 'crosshair' : 'default' }}
                                            >
                                                {isLoaded && cameraPosition && (
                                                    <GoogleMap center={cameraPosition} zoom={10} onLoad={handleLoad}>
                                                        <MapCameraOverlay
                                                            camera={toMapCameraFromCctv(cctvCamera)}
                                                            cameraAngle={cameraAngle}
                                                            cameraFOV={cameraFOV}
                                                            cameraRange={cameraRange}
                                                            isEditingLocation={isEditingLocation}
                                                            tempMarkerPosition={tempMarkerPosition}
                                                            onMarkerDragEnd={handleMarkerDragEnd}
                                                        />
                                                    </GoogleMap>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </div>
                        </section>
                    </Card>
                </div>
            )}
        </PageTemplate>
    );
};

export default CctvCameraEditForm;
